import { catchError, tap, throwError } from 'rxjs';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { isPlatformBrowser } from '@angular/common';
import { paramethorFallbackValues } from '~/paramethor/names.enum';
import { environment } from '~environments/environment';

interface ParamethorParamInterface {
  parameter_id: number;
  parameter_name: string;
  parameter_value: string;
  exception_id: number;
}

interface ParamethorFlagInterface {
  name: string;
  value: string;
}

export enum ParamethorContextType {
  COUNTRY_CODE = 'COUNTRY_CODE',
  AUTH_PROVIDER = 'AUTH_PROVIDER',
}

@Injectable({
  providedIn: 'root',
})
export class ParamethorService {
  private cachedFlags: ParamethorFlagInterface[] | null = null;

  constructor(
    @Inject(PLATFORM_ID) private readonly platformId: string,
    protected readonly httpClient: HttpClient
  ) {}

  private get isBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }

  private getCachedFlags() {
    if (this.cachedFlags !== null) {
      return this.cachedFlags;
    }

    if (!this.isBrowser) {
      return null;
    }

    const raw = sessionStorage.getItem('flags');
    if (!raw) {
      return null;
    }

    this.cachedFlags = JSON.parse(raw);
    return this.cachedFlags;
  }

  public initializeParamethor() {
    const flags = this.getCachedFlags();
    if (!flags) {
      this.load().subscribe();
    }
  }

  public load(
    contextType: ParamethorContextType = null,
    contextValue: string = null
  ) {
    const body =
      contextType && contextValue
        ? [{ context_type: contextType, context_value: contextValue }]
        : undefined;

    return this.httpClient
      .post(`${environment.applications.front.url}/paramethor/load`, body)
      .pipe(
        tap((data: ParamethorParamInterface[]) => {
          const paramDataArray: ParamethorFlagInterface[] = data.map(
            (item) => ({
              name: item.parameter_name,
              value: item.parameter_value,
            })
          );

          this.storeFlags(paramDataArray);
        }),
        catchError((err) => {
          console.error('Paramethor failed to load, using fallback values...');

          const fallbackFlags: ParamethorFlagInterface[] = Object.entries(
            paramethorFallbackValues
          ).map(([name, value]) => ({
            name,
            value,
          }));

          this.storeFlags(fallbackFlags);
          return throwError(() => err);
        })
      );
  }

  private storeFlags(data: ParamethorFlagInterface[]): void {
    this.cachedFlags = data;
    if (this.isBrowser) {
      sessionStorage.setItem('flags', JSON.stringify(data));
    }
  }

  public get(name: string, defaultValue?: string): string {
    const flags = this.getCachedFlags();

    if (!flags) {
      return defaultValue;
    }

    const flag = flags.find((item) => item.name === name);

    return flag?.value ?? defaultValue;
  }

  public getObject<T>(name: string, defaultValue?: T): T {
    const flagValue = this.get(name);
    if (!flagValue) {
      return defaultValue;
    }

    try {
      return JSON.parse(flagValue) as T;
    } catch (e) {
      console.error('ParamethorService: getObject JSON parse error', e);
      return defaultValue;
    }
  }

  public getNumber(name: string, defaultValue?: number): number {
    const flagValue = this.get(name) ?? defaultValue;
    const parsedValue = Number(flagValue);
    return Number.isNaN(parsedValue) ? defaultValue : parsedValue;
  }

  public isTrue(name: string, defaultValue?: boolean): boolean {
    const flagValue = this.get(name) ?? defaultValue;
    return flagValue === 'true' || flagValue === true;
  }

  public isDefined(name: string): boolean {
    const flagValue = this.get(name);
    return ![undefined, null, '', 'null', 'false'].includes(flagValue);
  }
}
